import { API_BASE } from "@/lib/utils";
import { getAuthHeaders } from "@/lib/auth-fetch";

const KIND_LABELS = {
  attendee: "Attendee",
  help: "Help",
  comms: "Comms",
  event: "Event",
  speaker: "Speaker",
};

export function aiSearchKindLabel(kind) {
  return KIND_LABELS[kind] || kind || "Result";
}

/** Merged SQL + vector search (backend RRF when plan includes ai_search). */
export async function fetchAdminSearch(query, { signal } = {}) {
  const q = String(query || "").trim();
  if (!q) return { items: [], aiMerged: false };
  const res = await fetch(`${API_BASE}/api/admin/search?q=${encodeURIComponent(q)}`, {
    cache: "no-store",
    headers: getAuthHeaders(),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Search failed (${res.status})`);
  }
  const payload = await res.json();
  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    aiMerged: Boolean(payload?.aiMerged),
  };
}

/** Vector-only search with optional scopes and event filter. */
export async function fetchAiSearch(query, { scopes, eventId, limit = 16, signal, timeoutMs = 7000 } = {}) {
  const q = String(query || "").trim();
  if (!q) return { items: [], mockMode: false };
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }
  const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${API_BASE}/api/admin/ai/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({
        q,
        scopes: scopes || ["attendees", "help", "comms", "events"],
        event_id: eventId || null,
        limit,
      }),
      signal: controller.signal,
    });
    if (res.status === 403) {
      return { items: [], planGated: true };
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.detail || `AI search failed (${res.status})`);
    }
    const payload = await res.json();
    return {
      items: Array.isArray(payload?.items) ? payload.items : [],
      mockMode: Boolean(payload?.mockMode),
      planGated: false,
    };
  } finally {
    if (timer) clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onAbort);
  }
}

export function normalizeSearchRow(row) {
  return {
    id: `${row.kind || "item"}-${row.id || row.href}`,
    kind: row.kind || "item",
    title: row.title || "Result",
    subtitle: row.subtitle || "",
    href: row.href || "/admin",
    ai: Boolean(row.ai),
    score: row.score,
  };
}
